// src/lib/utils/format/safe.ts
// AUTO-SPLIT from lib/utils/formatters.ts (A10/A2). New code: import from ~/lib/utils/format.
import { safeParseDate, formatDate as formatDateFromUtils, type DateFormat } from '~/lib/utils/date'
import type { SafeStringOptions, RelativeTimeOptions } from './types'
import { formatNumber, formatCurrency, formatPercent, formatCompact } from './numbers'
import { postProcessText } from './text'

/**
 * Helper function to format relative dates using the date utility.
 * Integrates with the existing date utility module for consistency.
 * 
 * @param {Date} date - The date to format relatively
 * @param {string} locale - The locale to use for formatting
 * @param {RelativeTimeOptions} [options] - Options for relative formatting
 * @returns {string} Formatted relative date string
 * @internal
 */
function formatRelativeDate(date: Date, locale: string, options?: RelativeTimeOptions): string {
  const { unit = 'auto', numeric = 'auto' } = options ?? {}
  
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric })
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    
    if (unit === 'auto') {
      const absDiffMs = Math.abs(diffMs)
      const diffSeconds = Math.floor(absDiffMs / 1000)
      const diffMinutes = Math.floor(diffSeconds / 60)
      const diffHours = Math.floor(diffMinutes / 60)
      const diffDays = Math.floor(diffHours / 24)
      const diffWeeks = Math.floor(diffDays / 7)
      const diffMonths = Math.floor(diffDays / 30)
      const diffYears = Math.floor(diffDays / 365)
      
      if (diffYears > 0) {
        return rtf.format(diffMs < 0 ? -diffYears : diffYears, 'year')
      } else if (diffMonths > 0) {
        return rtf.format(diffMs < 0 ? -diffMonths : diffMonths, 'month')
      } else if (diffWeeks > 0) {
        return rtf.format(diffMs < 0 ? -diffWeeks : diffWeeks, 'week')
      } else if (diffDays > 0) {
        return rtf.format(diffMs < 0 ? -diffDays : diffDays, 'day')
      } else if (diffHours > 0) {
        return rtf.format(diffMs < 0 ? -diffHours : diffHours, 'hour')
      } else if (diffMinutes > 0) {
        return rtf.format(diffMs < 0 ? -diffMinutes : diffMinutes, 'minute')
      } else {
        return rtf.format(diffMs < 0 ? -diffSeconds : diffSeconds, 'second')
      }
    } else {
      const unitMap = {
        second: 1000,
        minute: 60 * 1000,
        hour: 60 * 60 * 1000,
        day: 24 * 60 * 60 * 1000,
        week: 7 * 24 * 60 * 60 * 1000,
        month: 30 * 24 * 60 * 60 * 1000,
        year: 365 * 24 * 60 * 60 * 1000
      }
      
      const unitMs = unitMap[unit]
      const diff = Math.floor(diffMs / unitMs)
      return rtf.format(diff, unit)
    }
  } catch {
    // Fallback to basic relative description
    const diffMs = date.getTime() - new Date().getTime()
    const isPast = diffMs < 0
    const absDiffMs = Math.abs(diffMs)
    const diffDays = Math.floor(absDiffMs / (24 * 60 * 60 * 1000))
    
    if (diffDays === 0) {
      return 'today'
    } else if (diffDays === 1) {
      return isPast ? 'yesterday' : 'tomorrow'
    } else {
      return isPast ? `${diffDays} days ago` : `in ${diffDays} days`
    }
  }
}

/**
 * Helper function to format dates with custom format strings.
 * This is a basic implementation - for more advanced formatting, consider using a library like date-fns.
 * 
 * @param {Date} date - The date to format
 * @param {string} format - The format string
 * @returns {string} Formatted date string
 * @internal
 */
function formatCustomDate(date: Date, format: string): string {
  const pad = (num: number, length = 2): string => num.toString().padStart(length, '0')
  
  const tokens: Record<string, string> = {
    'YYYY': date.getFullYear().toString(),
    'YY': date.getFullYear().toString().slice(-2),
    'MM': pad(date.getMonth() + 1),
    'M': (date.getMonth() + 1).toString(),
    'DD': pad(date.getDate()),
    'D': date.getDate().toString(),
    'HH': pad(date.getHours()),
    'H': date.getHours().toString(),
    'hh': pad(date.getHours() % 12 || 12),
    'h': (date.getHours() % 12 || 12).toString(),
    'mm': pad(date.getMinutes()),
    'm': date.getMinutes().toString(),
    'ss': pad(date.getSeconds()),
    's': date.getSeconds().toString(),
    'a': date.getHours() >= 12 ? 'PM' : 'AM',
    'A': date.getHours() >= 12 ? 'PM' : 'AM'
  }
  
  let result = format
  // Sort by length descending to avoid partial replacements
  const sortedTokens = Object.keys(tokens).sort((a, b) => b.length - a.length)
  
  for (const token of sortedTokens) {
    const replacement = tokens[token]
    if (replacement !== undefined) {
      result = result.replace(new RegExp(token, 'g'), replacement)
    }
  }
  
  return result
}

// =============================================================================
// NUMBER FORMATTING FUNCTIONS
// =============================================================================


/**
 * Safely converts any value to a string, avoiding the '[object Object]' issue
 * and providing meaningful fallbacks for different data types.
 * 
 * This function is designed to handle the common TypeScript/ESLint error:
 * "'value' will use Object's default stringification format ('[object Object]') when stringified"
 *
 * @param {unknown} value - The value to convert to a string
 * @param {SafeStringOptions} [options] - Configuration options for string conversion
 * @returns {string} A safely converted string representation
 * 
 * @example
 * ```typescript
 * // Basic usage
 * safeString(null) // Returns ''
 * safeString(undefined) // Returns ''
 * safeString(123) // Returns '123'
 * safeString(true) // Returns 'true'
 * safeString({ name: 'John' }) // Returns '[Object]'
 * 
 * // With custom options
 * safeString(null, { fallback: 'N/A' }) // Returns 'N/A'
 * safeString({ id: 1, name: 'John' }, { tryJSON: true }) // Returns '{"id":1,"name":"John"}'
 * safeString('  long text  ', { trimWhitespace: true, maxLength: 10 }) // Returns 'long text'
 * 
 * // With number format
 * safeString(1234567, { 
 *   numberFormat: { 
 *     decimals: 2, 
 *     useGrouping: true, 
 *     style: 'currency', 
 *     currency: 'USD' 
 *   } 
 * }) // Returns '$1,234,567.00'
 * 
 * // Date formatting with multiple styles
 * safeString(new Date(), { 
 *   dateFormat: { 
 *     style: 'monthDay', 
 *     includeTime: true 
 *   } 
 * }) // Returns 'Feb 25, 2024, 3:30 PM'
 * 
 * // JSON stringification for complex objects
 * safeString({ id: 1, name: 'John' }, { tryJSON: true }) 
 * // Returns '{"id":1,"name":"John"}'
 * 
 * // Length limiting with custom truncation
 * safeString('very long text', { maxLength: 10, truncateEllipsis: '…' }) 
 * // Returns 'very long…'
 * ```
 */
export function safeString(value: unknown, options?: SafeStringOptions): string {
  const {
    fallback = '',
    objectFallback = '[Object]',
    arrayFallback = '[Array]',
    functionFallback = '[Function]',
    symbolFallback = '[Symbol]',
    preserveNumbers = true,
    preserveBooleans = true,
    preserveDates = true,
    tryJSON = false,
    maxLength,
    trimWhitespace = true,
    truncateEllipsis = '...',
    numberFormat,
    dateFormat
  } = options ?? {}

  // Handle null and undefined
  if (value === null || value === undefined) {
    return fallback
  }

  // Handle numbers with optional formatting
  if (typeof value === 'number') {
    if (preserveNumbers) {
      let result: string
      
      // Apply number formatting if options are provided
      if (numberFormat) {
        try {
          if (numberFormat.style === 'currency') {
            result = formatCurrency(value, {
              decimals: numberFormat.decimals,
              currency: numberFormat.currency,
              locale: numberFormat.locale
            })
          } else if (numberFormat.style === 'percent') {
            result = formatPercent(value, numberFormat.decimals ?? 1)
          } else if (numberFormat.style === 'compact') {
            result = formatCompact(value, numberFormat.decimals)
          } else {
            result = formatNumber(value, numberFormat.decimals, numberFormat.locale)
          }
        } catch {
          result = String(value)
        }
      } else {
        result = String(value)
      }
      
      return postProcessText(result, { maxLength, truncateEllipsis })
    }
    return fallback
  }

  // Handle strings with post-processing
  if (typeof value === 'string') {
    return postProcessText(value, { trimWhitespace, maxLength, truncateEllipsis })
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    if (preserveBooleans) {
      const result = String(value)
      return postProcessText(result, { maxLength, truncateEllipsis })
    }
    return fallback
  }

  // Handle functions
  if (typeof value === 'function') {
    return functionFallback
  }

  // Handle symbols
  if (typeof value === 'symbol') {
    return symbolFallback
  }

  // Handle arrays
  if (Array.isArray(value)) {
    if (tryJSON) {
      try {
        const result = JSON.stringify(value)
        return postProcessText(result, { trimWhitespace, maxLength, truncateEllipsis })
      } catch {
        // Fall through to arrayFallback
      }
    }
    return arrayFallback
  }

  // Handle objects (including Date, RegExp, etc.)
  if (typeof value === 'object') {
    // Special handling for Date objects
    if (value instanceof Date) {
      if (preserveDates && dateFormat) {
        try {
          const parsedDate = safeParseDate(value)
          if (parsedDate) {
            let result: string
            
            if (dateFormat.style === 'relative') {
              result = formatRelativeDate(parsedDate, dateFormat.locale ?? 'en-US', dateFormat.relativeOptions)
            } else if (dateFormat.style === 'custom' && dateFormat.customFormat) {
              result = formatCustomDate(parsedDate, dateFormat.customFormat)
            } else {
              // Map to your date utility's DateFormat type
              const mappedStyle = dateFormat.style ?? 'short' // Default to 'short'
              // Filter out unsupported styles for the date utility
              const supportedStyles: DateFormat[] = ['full', 'monthDay', 'shortMonthDay', 'shortMonthYear', 'monthYear', 'short', 'iso', 'datetime']
              const finalStyle = supportedStyles.includes(mappedStyle as DateFormat) ? mappedStyle as DateFormat : 'short'
              result = formatDateFromUtils(parsedDate, finalStyle)
            }
            
            return postProcessText(result, { maxLength, truncateEllipsis })
          }
        } catch {
          // Fall through to default ISO string
        }
      }
      
      // Default: return ISO string
      const result = value.toISOString()
      return maxLength && result.length > maxLength 
        ? result.slice(0, maxLength - truncateEllipsis.length) + truncateEllipsis
        : result
    }
    
    // Special handling for RegExp objects
    if (value instanceof RegExp) {
      const result = value.toString()
      return postProcessText(result, { maxLength, truncateEllipsis })
    }
    
    // Try JSON stringification if requested
    if (tryJSON) {
      try {
        const result = JSON.stringify(value)
        return postProcessText(result, { trimWhitespace, maxLength, truncateEllipsis })
      } catch {
        // Fall through to objectFallback
      }
    }

    return objectFallback
  }

  // Fallback for any other types
  return fallback
}

/**
 * Safely converts a value to a string suitable for use as a display label.
 * Convenience wrapper around safeString with sensible defaults for UI components.
 *
 * @param {unknown} value - The value to convert to a display label
 * @param {string} [fallback='—'] - Fallback text for null/undefined values
 * @param {number} [maxLength=100] - Maximum length before truncation
 * @returns {string} A safely converted display label
 * 
 * @example
 * ```typescript
 * safeLabel("Product Name") // Returns "Product Name"
 * safeLabel(null) // Returns "—"
 * safeLabel(null, "N/A") // Returns "N/A"
 * safeLabel({ id: 1, name: 'Product' }) // Returns "[Data]"
 * safeLabel("Very long product name that exceeds limit", "—", 10) // Returns "Very long..."
 * ```
 */
export function safeLabel(value: unknown, fallback = '—', maxLength = 100): string {
  return safeString(value, {
    fallback,
    objectFallback: '[Data]',
    arrayFallback: '[List]',
    maxLength,
    trimWhitespace: true
  })
}

/**
 * Safely converts a value to a string suitable for use as a chart key or identifier.
 * Ensures the result is always a valid string that can be used safely in contexts
 * where a string key is required.
 *
 * @param {unknown} value - The value to convert to a key
 * @param {string | number} [fallback] - Fallback value (will be converted to string)
 * @returns {string} A safely converted key string
 * 
 * @example
 * ```typescript
 * safeKey("product-123") // Returns "product-123"
 * safeKey(null, 0) // Returns "0"
 * safeKey({ id: 1 }, "object-key") // Returns "object-key"
 * safeKey(undefined) // Returns "key"
 * safeKey("") // Returns "key"
 * ```
 */
export function safeKey(value: unknown, fallback?: string | number): string {
  const fallbackStr = fallback !== undefined ? String(fallback) : 'key'
  
  const result = safeString(value, {
    fallback: fallbackStr,
    objectFallback: fallbackStr,
    arrayFallback: fallbackStr,
    functionFallback: fallbackStr,
    symbolFallback: fallbackStr,
    preserveNumbers: true,
    preserveBooleans: true,
    trimWhitespace: true,
    maxLength: 200 // Reasonable limit for keys
  })
  
  // Ensure we never return an empty string
  return result ?? fallbackStr
}

/**
 * Safely extracts a string value from an object property, with fallback handling.
 * This is particularly useful for data where property values might be of unknown types.
 *
 * @param {Record<string, unknown> | null | undefined} obj - The object to extract from
 * @param {string} key - The property key to extract
 * @param {string | number | ((obj: Record<string, unknown>) => unknown)} [fallback] - Fallback value if property is missing or invalid
 * @returns {string} Safely extracted and converted string value
 * 
 * @example
 * ```typescript
 * // Basic property extraction
 * safeObjectString({ name: "Product A", id: 123 }, "name")
 * // Returns "Product A"
 * 
 * // Fallback to another property using function
 * safeObjectString({ id: 123 }, "name", obj => obj.id)
 * // Returns "123"
 * 
 * // Static fallback
 * safeObjectString({}, "name", "Unknown")
 * // Returns "Unknown"
 * 
 * // Null object handling
 * safeObjectString(null, "name")
 * // Returns "Invalid Object"
 * ```
 */
export function safeObjectString(
  obj: Record<string, unknown> | null | undefined, 
  key: string, 
  fallback?: string | number | ((obj: Record<string, unknown>) => unknown)
): string {
  if (!obj || typeof obj !== 'object') {
    return safeString(fallback, { fallback: 'Invalid Object' })
  }
  
  const value = obj[key]
  
  if (value !== null && value !== undefined) {
    return safeString(value, { fallback: 'Invalid Value' })
  }
  
  // Handle function fallback
  if (typeof fallback === 'function') {
    try {
      const fallbackValue = fallback(obj)
      return safeString(fallbackValue, { fallback: 'Fallback Error' })
    } catch {
      return 'Fallback Error'
    }
  }
  
  // Handle direct fallback value
  return safeString(fallback, { fallback: 'No Value' })
}

// =============================================================================
// STRING FORMATTING FUNCTIONS
// =============================================================================

